import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import "./learningStyle.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { getLsQuestions } from "../../functions/getLsQuestions";
import { saveLsQuestion } from "../../functions/saveLsQuestion";
import { deleteLsQuestion } from "../../functions/deleteLsQuestion";
import { deleteLsQuestions } from "../../functions/deleteLsQuestions";
import { importLsQuestionsCSV, exportLsQuestionsCSV } from "../../functions/importLsQuestionsCSV";
import { useDocumentTitle } from "../../functions/useDocumentTitle";

Modal.setAppElement("#root");

const MAX_QUESTION_LEN = 350;
const MAX_OPTION_LEN = 100;
const OPTION_KEYS = ["option_one", "option_two", "option_three", "option_four", "option_five", "option_six"];

// This game scores by which option POSITION a player picks, not correctness — there is
// no real "correct answer" concept, so answer_id is always fixed to the first option
// (the backend schema still requires a value, but nothing reads it as meaningful).
const FIXED_ANSWER_ID = 1;

const emptyForm = () => ({
  id: null,
  question_name: "",
  show_option: 4,
  options: ["", "", "", "", "", ""],
});

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function Questions() {
  useDocumentTitle("Questions - Learning Style Admin");
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme } = useSelector((state) => state.theme);
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const csvInputRef = useRef(null);

  const loadQuestions = async () => {
    if (!currentTheme) return;
    setLoading(true);
    try {
      const res = await getLsQuestions({ token: adminToken, themeName: currentTheme });
      setQuestions(res.questions || []);
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to load questions", "error");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentTheme]);

  React.useEffect(() => {
    if (adminToken && currentTheme) loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, currentTheme]);

  const openAddModal = () => {
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEditModal = (q) => {
    setForm({
      id: q.id,
      question_name: q.question_name || "",
      show_option: q.show_option || 4,
      options: OPTION_KEYS.map((k) => q[k] || ""),
    });
    setModalOpen(true);
  };

  const handleOptionChange = (idx, val) => {
    if (val.length > MAX_OPTION_LEN) return;
    const opts = [...form.options];
    opts[idx] = val;
    setForm({ ...form, options: opts });
  };

  const handleSave = async () => {
    const name = form.question_name.trim();
    if (!name) {
      Swal.fire("Error", "Question text is required", "error");
      return;
    }
    if (name.length > MAX_QUESTION_LEN) {
      Swal.fire("Error", `Question must not exceed ${MAX_QUESTION_LEN} characters`, "error");
      return;
    }
    for (let i = 0; i < form.show_option; i++) {
      if (!form.options[i].trim()) {
        Swal.fire("Error", `Option ${i + 1} is required`, "error");
        return;
      }
    }

    const payload = {
      id: form.id,
      themeName: currentTheme,
      question_name: name,
      show_option: form.show_option,
      answer_id: FIXED_ANSWER_ID,
    };
    OPTION_KEYS.forEach((key, i) => {
      payload[key] = form.options[i];
    });

    setSaving(true);
    try {
      await saveLsQuestion({ payload, token: adminToken });
      Swal.fire("Success", form.id ? "Question updated" : "Question added", "success");
      setModalOpen(false);
      loadQuestions();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save question", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (q) => {
    Swal.fire({
      title: "Delete question?",
      text: q.question_name,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await deleteLsQuestion({ id: q.id, themeName: currentTheme, token: adminToken });
        loadQuestions();
      } catch (e) {
        Swal.fire("Error", e.message || "Failed to delete", "error");
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    Swal.fire({
      title: `Delete ${selectedRows.length} question(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete them",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await deleteLsQuestions({ ids: selectedRows.map((r) => r.id), themeName: currentTheme, token: adminToken });
        setSelectedRows([]);
        loadQuestions();
      } catch (e) {
        Swal.fire("Error", e.message || "Failed to delete", "error");
      }
    });
  };

  const handleCsvFileChange = (e) => {
    setCsvFile(e.target.files[0] || null);
  };

  const handleImportSubmit = async () => {
    if (!csvFile) {
      Swal.fire("Error", "Please choose a CSV file first", "error");
      return;
    }
    setImporting(true);
    try {
      const res = await importLsQuestionsCSV({ file: csvFile, themeName: currentTheme, token: adminToken });
      Swal.fire("Success", res.message || "Questions imported", "success");
      setCsvModalOpen(false);
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = "";
      loadQuestions();
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to import CSV", "error");
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportLsQuestionsCSV({ token: adminToken, themeName: currentTheme });
      downloadBlob(blob, "learning_style_questions.csv");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to export", "error");
    }
  };

  const handleDownloadSample = () => {
    const header = "question_name,option_one,option_two,option_three,option_four,option_five,option_six,correct_answer,answer_id,showOption";
    const sample = 'Which do you prefer?,Reading,Listening,Doing,Watching,,,Reading,1,4';
    const csv = `${header}\n${sample}\n`;
    downloadBlob(new Blob([csv], { type: "text/csv" }), "learning_style_questions_sample.csv");
  };

  const columns = useMemo(
    () => [
      { name: "Sr. No.", cell: (row, index) => index + 1, width: "90px" },
      { name: "Question", selector: (row) => row.question_name, wrap: true, grow: 2 },
      { name: "Options", selector: (row) => row.show_option },
      {
        name: "Actions",
        cell: (row) => (
          <div className="ls-admin-table-actions">
            <button type="button" className="ls-admin-icon-btn" onClick={() => openEditModal(row)}>
              <i className="fa-solid fa-pen" />
            </button>
            <button type="button" className="ls-admin-icon-btn ls-admin-icon-btn-danger" onClick={() => handleDelete(row)}>
              <i className="fa-solid fa-trash" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adminToken]
  );

  if (!currentTheme) {
    return (
      <div className="ls-admin-page">
        <div className="back-button-holder">
          <button type="button" className="back-button" onClick={() => navigate("/admin/theme")}>
            <i className="fa-solid fa-arrow-left" /> Back
          </button>
        </div>
        <p className="ls-admin-muted">No theme selected yet — go back and create or select a theme first.</p>
      </div>
    );
  }

  return (
    <div className="ls-admin-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <h2 className="ls-admin-title">Learning Style — Questions</h2>

      <div className="ls-admin-toolbar">
        <button type="button" className="ls-admin-btn-primary" onClick={openAddModal}>
          <i className="fa-solid fa-plus" /> Add Question
        </button>
        <button
          type="button"
          className="ls-admin-btn-secondary"
          onClick={() => setCsvModalOpen(true)}
          disabled={importing}
        >
          <i className="fa-solid fa-upload" /> Upload CSV
        </button>
        <button type="button" className="ls-admin-btn-secondary" onClick={handleExport}>
          Export CSV
        </button>
        <button type="button" className="ls-admin-btn-secondary" onClick={handleDownloadSample}>
          Sample CSV
        </button>
        <button
          type="button"
          className="ls-admin-btn-secondary"
          onClick={() => navigate("/admin/category-settings")}
        >
          <i className="fa-solid fa-gear" /> Labels Settings
        </button>
        {selectedRows.length > 0 && (
          <button type="button" className="ls-admin-btn-danger" onClick={handleBulkDelete}>
            Delete Selected ({selectedRows.length})
          </button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={questions}
        progressPending={loading}
        selectableRows
        onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
        pagination
        keyField="id"
        noDataComponent="No questions yet"
      />

      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        className="ls-admin-modal"
        overlayClassName="ls-admin-modal-overlay"
        contentLabel="Question"
      >
        <h3 className="ls-admin-card-title">{form.id ? "Edit Question" : "Add Question"}</h3>

        <label className="ls-admin-label">Question text</label>
        <textarea
          className="ls-admin-textarea"
          maxLength={MAX_QUESTION_LEN}
          value={form.question_name}
          onChange={(e) => setForm({ ...form, question_name: e.target.value })}
        />
        <p className="code">* Max length {MAX_QUESTION_LEN} characters</p>

        <label className="ls-admin-label">Number of options</label>
        <div className="ls-admin-option-count-row">
          {[2, 3, 4, 5, 6].map((n) => (
            <button
              key={n}
              type="button"
              className={`ls-admin-count-btn ${form.show_option === n ? "active" : ""}`}
              onClick={() => setForm({ ...form, show_option: n })}
            >
              {n}
            </button>
          ))}
        </div>

        {form.options.slice(0, form.show_option).map((opt, i) => (
          <div key={i} className="ls-admin-option-row">
            <input
              type="text"
              className="ls-admin-input"
              placeholder={`Option ${i + 1}`}
              maxLength={MAX_OPTION_LEN}
              value={opt}
              onChange={(e) => handleOptionChange(i, e.target.value)}
            />
          </div>
        ))}
        <p className="code">* Max length {MAX_OPTION_LEN} characters per option</p>

        <div className="ls-admin-modal-actions">
          <button type="button" className="ls-admin-btn-secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </button>
          <button type="button" className="ls-admin-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={csvModalOpen}
        onRequestClose={() => setCsvModalOpen(false)}
        className="ls-admin-modal"
        overlayClassName="ls-admin-modal-overlay"
        contentLabel="Upload CSV"
      >
        <h3 className="ls-admin-card-title">Upload CSV</h3>

        <input ref={csvInputRef} type="file" accept=".csv" className="ls-admin-input" onChange={handleCsvFileChange} />

        <p className="code">
          * Questions will be overwritten.
          <br />* Use only CSV files.
          <br />* Whitespace will be removed automatically.
          <br />* Question max length {MAX_QUESTION_LEN} characters, each option max length {MAX_OPTION_LEN} characters.
          <br />* Number of options must be between 2 and 6.
          <br />* answer_id must point to correct_answer, and correct_answer must exactly match one of the options — a mismatch will reject the whole file.
          <br />* Before uploading, check the Sample CSV for the correct format.
        </p>

        <div className="ls-admin-modal-actions">
          <button type="button" className="ls-admin-btn-secondary" onClick={() => setCsvModalOpen(false)}>
            Cancel
          </button>
          <button type="button" className="ls-admin-save-btn" onClick={handleImportSubmit} disabled={importing}>
            {importing ? "Uploading..." : "Upload"}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Questions;
